import { toast } from "react-hot-toast";
import { Info } from "lucide-react";

import { Button } from "components/ui";
import { LAB_ORDER_STATUS } from "features/labOrders/constants";
import { useDownloadLabOrder } from "features/labOrders/hooks/useDownloadLabOrder";
import { useLabOrder } from "features/labOrders/hooks/useLabOrder";
import { deriveLabOrderStatus } from "features/labOrders/utils/labOrderStatus";

import { LabOrderIbHandoff } from "../LabOrderIbHandoff/LabOrderIbHandoff";
import s from "./LabOrderDetails.module.css";
import { LabOrderHealthlinkStatus } from "./LabOrderHealthlinkStatus";
import { LabOrderMessagePreview } from "./LabOrderMessagePreview";
import { LabOrderRejectionNotice } from "./LabOrderRejectionNotice";
import { LabOrderWhatWasOrdered } from "./LabOrderWhatWasOrdered";

// One order: the message itself, then where it stands with Healthlink, in the same
// shape a CDM review preview ends on.
//
// The order's facts are not repeated as a field grid above — they are all in the
// message the preview renders, and the list the GP arrived from already showed them.
// The status lives in the Healthlink section at the bottom, not in a pill at the top.
export const LabOrderDetails = ({ order: listOrder, onOrderCompleted }) => {
  // The list row is shown immediately; the fetch refreshes it, because an
  // acknowledgement may have arrived since the list was cached.
  const { data: fetchedOrder } = useLabOrder(listOrder?.id);
  const order = fetchedOrder ?? listOrder;

  const { mutate: downloadLabOrder, isPending: isChecking } =
    useDownloadLabOrder();

  const status = deriveLabOrderStatus(order);
  const isAwaitingCompletion = status === LAB_ORDER_STATUS.awaitingCompletion;

  const handleCheckCompletedOrder = () => {
    downloadLabOrder(order.id, {
      onSuccess: (result) => {
        if (result?.downloaded) {
          toast.success("Healthlink has the completed order.");

          return;
        }

        toast.error(
          "Healthlink doesn't have the completed order yet. Finish it in Healthlink, then check again."
        );
      },
      onError: (error) => {
        console.error("Failed to check the completed lab order", error);
        toast.error("We couldn't check this order with Healthlink. Try again.");
      },
    });
  };

  return (
    <div className={s.details}>
      {/* An unfinished order leads with the way to finish it — the same hand-over box
          the referral flow ends on, bordered here because on the order's own page it is
          a section rather than a step. Resuming is possible at all because the backend
          stores the Integrated Browser URL on the order (KIN-1531): Healthlink hands
          that URL back only once, at submission. */}
      {!!isAwaitingCompletion && !!order.integratedBrowserUrl && (
        <LabOrderIbHandoff
          submission={order}
          onFinished={onOrderCompleted ?? (() => {})}
        />
      )}

      {/* What was ordered, as data, above the document it was read from (KIN-1976). The preview
          below is Healthlink's own stylesheet and is unchanged — this does not replace it, since
          the printout is what the GP knows and carries the barcode. */}
      <LabOrderWhatWasOrdered order={order} />

      <LabOrderMessagePreview messageControlId={order.messageControlId} />

      {/* A work-list removal must reach the GP with the laboratory's own reason — the
          National GP Laboratory Ordering Specification requires it, so this stays even
          though the status itself now lives in the Healthlink section below. */}
      <LabOrderRejectionNotice order={order} />

      {!!isAwaitingCompletion && !order.integratedBrowserUrl && (
        <section className={s.notice}>
          <Info className={s.noticeIcon} size={20} aria-hidden="true" />
          <div>
            <p className={s.noticeText}>
              This order hasn&apos;t been completed in Healthlink, and no Healthlink
              link was stored for it. Create a new lab order to continue.
            </p>
            <Button
              variant="outline"
              size="small"
              onClick={handleCheckCompletedOrder}
              disabled={isChecking}
            >
              {isChecking ? "Checking…" : "Check for completed order"}
            </Button>
          </div>
        </section>
      )}

      <LabOrderHealthlinkStatus order={order} />
    </div>
  );
};
